/**
 * ChatDock - Floating bottom-right chat widget (desktop only)
 * Hidden on mobile via `hidden md:block` - users use navigation icon instead
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useChat, useConversationDraft } from '../context/ChatProvider';
import { ConversationList } from './ConversationList';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { NewChatModal } from './NewChatModal';

export const ChatDock: React.FC = () => {
  const location = useLocation();
  const {
    conversations,
    messagesByConversationId,
    activeConversationId,
    unreadTotal,
    isLoading,
    loadingConversationId,
    isSendingMessage,
    openConversation,
    sendMessage,
    startNewChat,
    loadConversations,
    registerChatSurface,
    unregisterChatSurface,
  } = useChat();

  const dockRef = useRef<HTMLDivElement>(null);
  const messageScrollRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);

  const activeConversation = conversations.find((c) => c.id === activeConversationId);
  const activeMessages = activeConversationId
    ? messagesByConversationId[activeConversationId] || []
    : [];
  const isLoadingMessages = activeConversationId === loadingConversationId;
  const isChatPage = location.pathname === '/chat';
  const isMessagePaneVisible = isExpanded && !isChatPage;
  const { draft, setDraft } = useConversationDraft(activeConversationId);

  const handleToggle = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    
    if (newExpandedState) {
      // Expanding - load conversations. Polling is registered by the visibility effect below.
      loadConversations();
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    openConversation(conversationId);
    setShowMessages(true);
  };

  const handleBackToList = () => {
    setShowMessages(false);
  };

  const handleSendMessage = async (body: string) => {
    if (!activeConversationId) return;
    await sendMessage(activeConversationId, body);
  };

  const handleNewChat = async (friendUserId: string) => {
    await startNewChat(friendUserId);
    setShowMessages(true);
  };

  useEffect(() => {
    if (!isMessagePaneVisible) return;

    registerChatSurface();
    loadConversations();

    return () => {
      unregisterChatSurface();
    };
  }, [isMessagePaneVisible, registerChatSurface, unregisterChatSurface, loadConversations]);

  useEffect(() => {
    if (isChatPage) {
      setIsNewChatModalOpen(false);
    }
  }, [isChatPage]);

  return (
    <div 
      ref={dockRef}
      data-chat-dock
      className="hidden md:block fixed bottom-4 right-4 z-50"
    >
      {!isMessagePaneVisible ? (
        /* Collapsed State */
        <button
          onClick={handleToggle}
          className="flex items-center space-x-2 bg-white border-2 border-blue-500 text-blue-500 px-4 py-3 rounded-full shadow-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
        >
          <svg
            className="w-5 h-5 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <span className="font-medium text-blue-500">Chat</span>
          {unreadTotal > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
              {unreadTotal > 99 ? '99+' : unreadTotal}
            </span>
          )}
        </button>
      ) : (
        /* Expanded State */
        <div className="bg-white rounded-lg shadow-2xl flex flex-col animate-slide-up w-96 h-[600px]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            {/* Left side - Back button + Friend info or Title */}
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {showMessages && (
                <button
                  onClick={handleBackToList}
                  className="p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
                  aria-label="Back to conversations"
                >
                  <svg
                    className="w-5 h-5 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
              )}
              
              {showMessages && activeConversation ? (
                /* Active conversation - show friend info */
                <>
                  {activeConversation.friendAvatar ? (
                    <img
                      src={activeConversation.friendAvatar}
                      alt={activeConversation.friendName}
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs text-gray-600">
                        {activeConversation.friendName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {activeConversation.friendName}
                    </h3>
                    {activeConversation.friendUsername && (
                      <p className="text-xs text-gray-500 truncate">
                        @{activeConversation.friendUsername}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                /* Conversation list - show title + new chat button */
                <>
                  <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
                  <button
                    onClick={() => setIsNewChatModalOpen(true)}
                    className="p-1 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 ml-auto"
                    title="New Chat"
                    aria-label="Start new chat"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {/* Right side - Close button */}
            <button
              onClick={handleToggle}
              className="p-1 rounded-full hover:bg-gray-100 ml-2 flex-shrink-0"
              aria-label="Close chat"
            >
              <svg
                className="w-5 h-5 text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          {!showMessages ? (
            /* Conversations List */
            <div className="flex-1 overflow-y-auto min-h-0">
              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                isLoading={isLoading}
              />
            </div>
          ) : activeConversation ? (
            /* Active Conversation */
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              <div 
                ref={messageScrollRef}
                className="flex-1 min-h-0 overflow-y-auto"
              >
                <MessageList 
                  messages={activeMessages} 
                  isLoading={isLoadingMessages}
                  scrollContainerRef={messageScrollRef}
                />
              </div>
              <div className="flex-shrink-0">
                <MessageInput
                  onSend={handleSendMessage}
                  value={draft}
                  onChange={setDraft}
                  isSending={isSendingMessage}
                  autoFocus={true}
                  disabled={activeConversation.isActive === false}
                  disabledMessage="You are no longer friends with this user. Messages cannot be sent."
                />
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* New Chat Modal */}
      <NewChatModal
        isOpen={isNewChatModalOpen}
        onClose={() => setIsNewChatModalOpen(false)}
        onSelectFriend={handleNewChat}
      />
    </div>
  );
};
